import React, { useEffect, useState } from "react";
import MerchantPageShell from "../../components/merchant/MerchantPageShell";
import { getCurrentUser } from "../../lib/authApi";
import { getMyBoutiques, updateBoutique } from "../../lib/boutiqueApi";
import { apiFetch } from "../../lib/api";
import { useSession } from "../../hooks/useSession";
import { COUNTRIES } from "../../constants/countries";

export default function MerchantSettings() {
  const { refreshSession, logoutMerchant } = useSession();

  const [loading, setLoading] = useState(true);

  const [merchant, setMerchant] = useState(null);
  const [boutiqueId, setBoutiqueId] = useState(null);

  const [email, setEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [savingBoutique, setSavingBoutique] = useState(false);

  const [accountError, setAccountError] = useState("");
  const [accountSuccess, setAccountSuccess] = useState("");
  const [boutiqueError, setBoutiqueError] = useState("");
  const [boutiqueSuccess, setBoutiqueSuccess] = useState("");

const [boutiqueForm, setBoutiqueForm] = useState({
  boutiqueName: "",
  websiteUrl: "",
  description: "",
  country: "",
});

  useEffect(() => {
    async function loadSettings() {
      setLoading(true);
      setAccountError("");
      setAccountSuccess("");
      setBoutiqueError("");
      setBoutiqueSuccess("");

      try {
        const [merchantData, boutiques] = await Promise.all([
          getCurrentUser(),
          getMyBoutiques(),
        ]);

        setMerchant(merchantData || null);
        setEmail(merchantData?.email || "");

        const boutique =
          Array.isArray(boutiques) && boutiques.length ? boutiques[0] : null;

        if (boutique) {
          setBoutiqueId(boutique.id);
          setBoutiqueForm({
            boutiqueName: boutique.name || "",
            websiteUrl: boutique.website_url || "",
            description: boutique.description || "",
            country: boutique.country || "",
          });
        }
      } catch (err) {
        console.error("Failed to load merchant settings", err);
        setAccountError(err?.message || "Could not load merchant settings.");
      } finally {
        setLoading(false);
      }
    }

    loadSettings();
  }, []);

  function updateBoutiqueField(key, value) {
    setBoutiqueForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  async function handleEmailUpdate(e) {
    e.preventDefault();

    setAccountError("");
    setAccountSuccess("");
    setEmailLoading(true);

    try {
      await apiFetch("/auth/me", {
        method: "PATCH",
        body: JSON.stringify({
          email: email.trim(),
        }),
      });

      const refreshedMerchant = await getCurrentUser();
      setMerchant(refreshedMerchant || null);
      setEmail(refreshedMerchant?.email || email.trim());

      await refreshSession();
      setAccountSuccess("Email updated.");
    } catch (err) {
      console.error("Failed to update email", err);
      setAccountError(err?.message || "Could not update email.");
    } finally {
      setEmailLoading(false);
    }
  }

  async function handlePasswordChange(e) {
    e.preventDefault();

    setAccountError("");
    setAccountSuccess("");
    setPasswordLoading(true);

    try {
      await apiFetch("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      setCurrentPassword("");
      setNewPassword("");
      setAccountSuccess("Password updated.");
    } catch (err) {
      console.error("Failed to change password", err);
      setAccountError(err?.message || "Could not change password.");
    } finally {
      setPasswordLoading(false);
    }
  }

  async function handleSaveBoutique(e) {
    e.preventDefault();

    if (!boutiqueId) {
      setBoutiqueError("No boutique was found for this merchant account.");
      return;
    }

    const payload = {
      name: boutiqueForm.boutiqueName.trim(),
      website_url: boutiqueForm.websiteUrl.trim() || null,
      description: boutiqueForm.description.trim() || null,
      country: boutiqueForm.country,
    };

    if (!payload.name) {
      setBoutiqueError("Boutique name is required.");
      return;
    }

    if (!payload.country) {
  setBoutiqueError("Country is required.");
  return;
}

    setSavingBoutique(true);
    setBoutiqueError("");
    setBoutiqueSuccess("");

    try {
      const updated = await updateBoutique(boutiqueId, payload);

    setBoutiqueForm({
      boutiqueName: updated?.name || payload.name,
      websiteUrl: updated?.website_url || payload.website_url || "",
      description: updated?.description || payload.description || "",
      country: updated?.country || payload.country || "",
    });

      setBoutiqueSuccess("Boutique details updated.");
    } catch (err) {
      console.error("Failed to update boutique", err);
      setBoutiqueError(err?.message || "Could not update boutique details.");
    } finally {
      setSavingBoutique(false);
    }
  }

  return (
    <MerchantPageShell
      eyebrow="Merchant Settings"
      title="Settings"
      lede="Manage your account and boutique details."
    >
      <style>{`
        .merchant-settings-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 18px;
        }

        .merchant-settings-stack {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .merchant-settings-form {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-top: 6px;
        }

        .merchant-settings-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .merchant-settings-label {
          font-size: 13px;
          font-weight: 600;
          color: rgba(40, 40, 40, 0.78);
        }

        .merchant-settings-input,
        .merchant-settings-textarea,
        .merchant-settings-readonly {
          width: 100%;
          border-radius: 14px;
          border: 1px solid rgba(40, 40, 40, 0.08);
          background: rgba(255, 255, 255, 0.82);
          padding: 12px 14px;
          font-size: 14px;
          color: rgba(20, 20, 20, 0.92);
          outline: none;
          box-sizing: border-box;
        }

        .merchant-settings-input:focus,
        .merchant-settings-textarea:focus {
          border-color: rgba(106, 30, 45, 0.26);
          box-shadow: 0 0 0 3px rgba(106, 30, 45, 0.08);
        }

        .merchant-settings-textarea {
          min-height: 120px;
          resize: vertical;
          font-family: inherit;
        }

        .merchant-settings-readonly {
          background: rgba(0, 0, 0, 0.035);
          color: rgba(40, 40, 40, 0.72);
        }

        .merchant-settings-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 4px;
        }

        .merchant-settings-divider {
          height: 1px;
          background: rgba(40, 40, 40, 0.08);
          margin: 4px 0;
        }

        .merchant-settings-error {
          font-size: 13px;
          color: #b4232c;
        }

        .merchant-settings-success {
          font-size: 13px;
          color: #2f6f45;
        }

        .merchant-settings-note {
          font-size: 13px;
          color: rgba(40, 40, 40, 0.62);
          line-height: 1.5;
        }

        .merchant-settings-logout {
          font-size: 13px;
          cursor: pointer;
          color: rgba(40,40,40,0.72);
          transition: color 0.15s ease;
          user-select: none;
        }

        .merchant-settings-logout:hover {
          color: #6a1e2d;
        }

        @media (max-width: 980px) {
          .merchant-settings-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {loading ? (
        <div className="public-card">
          <div className="public-card-title">Loading settings</div>
          <div className="public-card-sub">
            Fetching merchant account and boutique details.
          </div>
        </div>
      ) : (
        <div className="merchant-settings-grid">
          <div className="merchant-settings-stack">
            <div className="public-card">
              <div className="public-card-title">Account</div>
              <div className="public-card-sub">
                Update your merchant login details.
              </div>

              <form className="merchant-settings-form" onSubmit={handleEmailUpdate}>
                <div className="merchant-settings-field">
                  <label className="merchant-settings-label">Email</label>
                  <input
                    className="merchant-settings-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="hello@yourstore.com"
                  />
                </div>

                <div className="merchant-settings-field">
                  <label className="merchant-settings-label">Account name</label>
                  <div className="merchant-settings-readonly">
                    {merchant?.name || "No account name found"}
                  </div>
                </div>

                <div className="merchant-settings-actions">
                  <button
                    className="gold-btn"
                    type="submit"
                    disabled={emailLoading}
                  >
                    {emailLoading ? "Updating..." : "Update email"}
                  </button>
                </div>
              </form>

              <div className="merchant-settings-divider" />

              <form className="merchant-settings-form" onSubmit={handlePasswordChange}>
                <div className="merchant-settings-field">
                  <label className="merchant-settings-label">Current password</label>
                  <input
                    className="merchant-settings-input"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter your current password"
                  />
                </div>

                <div className="merchant-settings-field">
                  <label className="merchant-settings-label">New password</label>
                  <input
                    className="merchant-settings-input"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Create a new password"
                  />
                </div>

                <div className="merchant-settings-actions">
                  <button
                    className="gold-btn"
                    type="submit"
                    disabled={passwordLoading}
                  >
                    {passwordLoading ? "Updating..." : "Change password"}
                  </button>
                </div>
              </form>

              {accountError ? (
                <div className="merchant-settings-error">{accountError}</div>
              ) : null}

              {accountSuccess ? (
                <div className="merchant-settings-success">{accountSuccess}</div>
              ) : null}

              <div className="merchant-settings-actions" style={{ marginTop: 8 }}>
                <span
                  className="merchant-settings-logout"
                  onClick={logoutMerchant}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      logoutMerchant();
                    }
                  }}
                >
                  Log out
                </span>
              </div>
            </div>
          </div>

          <div className="public-card">
            <div className="public-card-title">Boutique details</div>
            <div className="public-card-sub">
              Update the public-facing boutique information shown inside Veya.
            </div>

            <form className="merchant-settings-form" onSubmit={handleSaveBoutique}>
              <div className="merchant-settings-field">
                <label className="merchant-settings-label">Boutique name</label>
                <input
                  className="merchant-settings-input"
                  value={boutiqueForm.boutiqueName}
                  onChange={(e) =>
                    updateBoutiqueField("boutiqueName", e.target.value)
                  }
                  placeholder="Atelier Edit"
                />
              </div>

              <div className="merchant-settings-field">
                <label className="merchant-settings-label">Website</label>
                <input
                  className="merchant-settings-input"
                  value={boutiqueForm.websiteUrl}
                  onChange={(e) =>
                    updateBoutiqueField("websiteUrl", e.target.value)
                  }
                  placeholder="https://yourstore.com"
                />
              </div>

              <div className="merchant-settings-field">
  <label className="merchant-settings-label">Country</label>
  <select
    className="merchant-settings-input"
    value={boutiqueForm.country}
    onChange={(e) =>
      updateBoutiqueField("country", e.target.value)
    }
  >
    <option value="">Select country</option>
{COUNTRIES.map((country) => (
  <option key={country.code} value={country.code}>
    {country.label}
  </option>
))}
  </select>
</div>

              <div className="merchant-settings-field">
                <label className="merchant-settings-label">Description</label>
                <textarea
                  className="merchant-settings-textarea"
                  value={boutiqueForm.description}
                  onChange={(e) =>
                    updateBoutiqueField("description", e.target.value)
                  }
                  placeholder="Contemporary womenswear focused on natural fibres and relaxed tailoring."
                />
              </div>

              {boutiqueError ? (
                <div className="merchant-settings-error">{boutiqueError}</div>
              ) : null}

              {boutiqueSuccess ? (
                <div className="merchant-settings-success">{boutiqueSuccess}</div>
              ) : null}

              <div className="merchant-settings-actions">
                <button
                  className="gold-btn"
                  type="submit"
                  disabled={savingBoutique}
                >
                  {savingBoutique ? "Saving..." : "Save boutique details"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </MerchantPageShell>
  );
}